import moment from 'moment';
import { runInAction } from 'mobx';
import { AxiosResponse } from 'axios';
import { notUndefined } from 'ui/base/types';
import { createRequestString, getResponseBytes, http, isHttpError } from 'services/base/http';
import {
  GetHostResponse,
  GetHostsResponse,
  HostDeviceType,
  HostObjectMap,
  HostObjectMapTuple,
  HostObject,
  SetHostsRequest,
} from './protos/hosts_pb';
import { DeviceType, Host, HostMap, IPangolinDomain, NetworkThroughputDuration } from './types';
import { Optional, ResultKind, ServiceStatus } from 'services/base/types';

type PartialHostWithID = Pick<Host, 'id'> & Partial<Host>;

export type HostsUpdateResult =
  | { kind: ResultKind.OK; host: PartialHostWithID }
  | { kind: ResultKind.SYSTEM_ERROR; status?: number }
  | { kind: ResultKind.USER_ERROR; status?: number };

export type HostsUpdateAllResult =
  | { kind: ResultKind.OK; hosts: PartialHostWithID[] }
  | { kind: ResultKind.SYSTEM_ERROR; status?: number }
  | { kind: ResultKind.USER_ERROR; status?: number };

export class HostsService {
  static async fetch(domain: IPangolinDomain, id: string, save = true): Promise<Optional<Host>> {
    const httpResp = await http.get(`/pangolin/${domain.id}/hosts/${id}`);
    return runInAction(() => {
      const remoteHost = parseHostResponse(httpResp);
      if (remoteHost == null) return undefined;

      if (save) {
        const localHost = domain.hosts.get(id);
        if (localHost == null) {
          domain.hosts.set(remoteHost.id, remoteHost);
          domain.hosts.sort(hostSorter);
        } else {
          Object.assign(localHost, remoteHost);
        }
      }

      return remoteHost;
    });
  }

  static async fetchAll(domain: IPangolinDomain, save = true): Promise<HostMap> {
    const httpResp = await http.get(`/pangolin/${domain.id}/hosts`);
    const remoteHosts = parseHostsResponse(httpResp);
    remoteHosts.sort(hostSorter);
    return runInAction(() => {
      if (save) domain.hosts = remoteHosts;
      return remoteHosts;
    });
  }

  static async update(domain: IPangolinDomain, host: PartialHostWithID): Promise<HostsUpdateResult> {
    const request = createSetHostsRequest([host]);
    try {
      await http.put(`/pangolin/${domain.id}/hosts`, createRequestString(request));
      const remoteHost = await this.fetch(domain, host.id, false);
      return runInAction(() => {
        if (remoteHost != null) {
          const remotePartial = updatePartialFromComplete(host, remoteHost);
          updatePersistentFromPartial(domain, remotePartial);
          return { kind: ResultKind.OK, host: remotePartial };
        }

        return { kind: ResultKind.USER_ERROR, status: 404 };
      });
    } catch (error) {
      const status = isHttpError(error) ? error.response?.status : undefined;
      const kind = status && status < 500 ? ResultKind.USER_ERROR : ResultKind.SYSTEM_ERROR;
      return { kind, status };
    }
  }

  static async updateAll(domain: IPangolinDomain, hosts: PartialHostWithID[]): Promise<HostsUpdateAllResult> {
    const request = createSetHostsRequest(hosts);
    try {
      await http.put(`/pangolin/${domain.id}/hosts`, createRequestString(request));
      const remoteHosts = await this.fetchAll(domain, false);
      return runInAction(() => {
        const remotePartials = hosts.map((host) => {
          const remoteHost = remoteHosts.get(host.id);

          if (remoteHost != null) {
            const remotePartial = updatePartialFromComplete(host, remoteHost);
            updatePersistentFromPartial(domain, remotePartial);
            return remotePartial;
          }

          return undefined;
        }).filter(notUndefined);

        return { kind: ResultKind.OK, hosts: remotePartials };
      });
    } catch (error) {
      const status = isHttpError(error) ? error.response?.status : undefined;
      const kind = status && status < 500 ? ResultKind.USER_ERROR : ResultKind.SYSTEM_ERROR;
      return { kind, status };
    }
  }
}

function parseHostResponse(httpResp: AxiosResponse): Optional<Host> {
  const rawBytes = getResponseBytes(httpResp);
  const rootProto = GetHostResponse.deserializeBinary(rawBytes);
  const itemProto = rootProto.getHost();
  return hostFromProto(itemProto);
}

function parseHostsResponse(httpResp: AxiosResponse): HostMap {
  const rawBytes = getResponseBytes(httpResp);
  const rootProto = GetHostsResponse.deserializeBinary(rawBytes);
  const listProto = rootProto.getHosts().getItemsList();
  const hostObjects = listProto.map((p) => hostFromProto(p.getData()));
  return new HostMap(hostObjects.filter(notUndefined).map((h) => [h.id, h]));
}

function updatePersistentFromPartial(domain: IPangolinDomain, partial: PartialHostWithID): void {
  const target = domain.hosts.get(partial.id);
  if (target != null) Object.assign(target, partial);
}

function updatePartialFromComplete(partial: PartialHostWithID, complete: Host): PartialHostWithID {
  const updated: PartialHostWithID = Object.keys(partial).reduce((result, key) => {
    if (Object.hasOwn(complete, key)) {
      (result as Record<string, unknown>)[key] = (complete as Record<string, unknown>)[key];
    }
    return result;
  }, { id: partial.id });

  if (Object.hasOwn(updated, 'customName')) {
    updated.name = complete.name;
  }

  if (Object.hasOwn(updated, 'customDeviceType')) {
    updated.deviceType = complete.deviceType;
  }

  if (Object.hasOwn(updated, 'customOS')) {
    updated.machineOS = complete.machineOS;
    updated.humanReadableOS = complete.humanReadableOS;
  }

  return updated;
}

function createSeen(seen: Optional<string>): Optional<Date> {
  if (seen == null) {
    return undefined;
  } else {
    return moment.utc(seen).toDate();
  }
}

function hostFromProto(proto: Optional<HostObject>): Optional<Host> {
  if (proto === undefined) return;

  const id = proto.getId();

  if (id === undefined) return;

  return {
    id,
    mac: proto.getMac() ?? '',
    ip: proto.getIp() ?? '',
    gateway: proto.getGateway() ?? false,
    name: proto.getName() ?? '',
    machineOS: proto.getOs() ?? '',
    humanReadableOS: proto.getOsHuman() ?? '',
    deviceType: proto.getDevice() ?? HostDeviceType.H_MISC,
    lastSeen: createSeen(proto.getSeen()),
    uploadAllocation: proto.getAllocUpload() ?? 0.00,
    downloadAllocation: proto.getAllocDownload() ?? 0.00,
    customName: (proto.getUserName()?.length || 0) > 0 ?
                proto.getUserName() : undefined,
    customDeviceType: proto.getUserDevice() !== HostDeviceType.H_UNKNOWN ?
                      proto.getUserDevice() : undefined,
    customOS: (proto.getUserOs()?.length || 0) > 0 ?
              proto.getUserOs() : undefined,
    blockInternet: proto.getBlockInternet(),
    blockInternal: proto.getBlockInternal(),
    blockAdTracking: proto.getBlockAdtracking(),
    blockMalicious: proto.getBlockMalicious(),
    ignoreSchedules: proto.getIgnoreSchedules(),
    ignoreAll: proto.getIgnoreAll(),
    notifyOnUp: proto.getNotifyOnUp(),
    masquerade: proto.getMasquerade(),
    isOnline: proto.getUp() ?? false,
    throughput: {
      [NetworkThroughputDuration.HOURLY]: { status: ServiceStatus.FRESH },
      [NetworkThroughputDuration.DAILY]: { status: ServiceStatus.FRESH },
      [NetworkThroughputDuration.WEEKLY]: { status: ServiceStatus.FRESH },
    },
  };
}

function createSetHostsRequest(hosts: PartialHostWithID[]): SetHostsRequest {
  const map = new HostObjectMap();
  hosts.forEach((host) => {
    const tuple = createHostObjectMapTuple(host);
    map.addItems(tuple);
  });

  const request = new SetHostsRequest();
  request.setHosts(map);

  return request;
}

function createHostObjectMapTuple(host: PartialHostWithID): HostObjectMapTuple {
  const proto = new HostObject();
  if (host.uploadAllocation != null) {
    proto.setAllocUpload(host.uploadAllocation);
  }
  if (host.downloadAllocation != null) {
    proto.setAllocDownload(host.downloadAllocation);
  }
  if (Object.hasOwn(host, 'customName')) {
    proto.setUserName(host.customName ?? '');
  }
  if (Object.hasOwn(host, 'customOS')) {
    proto.setUserOs(host.customOS ?? '');
  }
  if (Object.hasOwn(host, 'customDeviceType')) {
    proto.setUserDevice(host.customDeviceType ??
                        HostDeviceType.H_UNKNOWN);
  }
  if (host.blockInternet != null) {
    proto.setBlockInternet(host.blockInternet);
  }
  if (host.blockInternal != null) {
    proto.setBlockInternal(host.blockInternal);
  }
  if (host.blockAdTracking != null) {
    proto.setBlockAdtracking(host.blockAdTracking);
  }
  if (host.blockMalicious != null) {
    proto.setBlockMalicious(host.blockMalicious);
  }
  if (host.ignoreSchedules != null) {
    proto.setIgnoreSchedules(host.ignoreSchedules);
  }
  if (host.ignoreAll != null) {
    proto.setIgnoreAll(host.ignoreAll);
  }
  if (host.notifyOnUp != null) {
    proto.setNotifyOnUp(host.notifyOnUp);
  }
  if (host.masquerade != null) {
    proto.setMasquerade(host.masquerade);
  }

  const tuple = new HostObjectMapTuple();
  tuple.setId(host.id);
  tuple.setData(proto);

  return tuple;
}

export function hostDisplayName(host: Host): string {
  if (host.customName && host.customName.trim().length > 0) {
    return host.customName;
  } else {
    return host.name;
  }
}

type HostDisplayDeviceTypeOpts = {
  overrideCustomDeviceType: true;
  customDeviceType: Optional<DeviceType>;
};

export function hostDisplayDeviceType(host: Host, opts?: HostDisplayDeviceTypeOpts): DeviceType {
  // This is overridden on the edit device page when the user gets to input their preferred
  // device type
  let customDeviceType = host.customDeviceType;
  if (opts?.overrideCustomDeviceType) {
    customDeviceType = opts.customDeviceType;
  }

  if (customDeviceType != null) {
    return customDeviceType;
  } else if (host.gateway) {
    return HostDeviceType.H_NETWORK;
  } else {
    return host.deviceType;
  }
}

export function hostMatchesSearchString(host: Host, rawSearch: string) {
  let search = rawSearch.trim();
  if (search) {
    search = search.toLocaleLowerCase();
    if (hostDisplayName(host).toLocaleLowerCase().indexOf(search) >= 0) {
      return true;
    } else if (host.mac && host.mac.toLocaleLowerCase().indexOf(search) >= 0) {
      return true;
    } else if (host.ip && host.ip.toLocaleLowerCase().indexOf(search) >= 0) {
      return true;
    }
    return false;
  }
  return true;
}

function hostSorter(a: Host, b: Host): number {
  // Online things go first
  // "Online devices" are devices seen within the last 5 minutes.
  if (a.isOnline !== b.isOnline) {
    return a.isOnline ? -1 : 1;
  }

  // In case of ties,
  const aName = a.name.toUpperCase();
  const bName = b.name.toUpperCase();
  const nameCompare = aName.localeCompare(bName);

  const aSeen = a.lastSeen?.getTime() ?? 0;
  const bSeen = b.lastSeen?.getTime() ?? 0;
  const seenCompare = aSeen > bSeen ? -1 : aSeen < bSeen ? 1 : 0;

  if (a.isOnline) {
    // Online devices: sort by name.
    if (nameCompare) {
      return nameCompare;
    }
  }

  if (!a.isOnline) {
    // Non-online devices: seen recently goes first.
    if (seenCompare) {
      return seenCompare;
    }
  }

  // Use the other tie-breaker in case it goes through.
  if (seenCompare) {
    return seenCompare;
  }
  if (nameCompare) {
    return nameCompare;
  }

  return 0;
}

export function countConnectedDevices(hosts: HostMap) {
  return hosts.filter((host) => host.isOnline).size;
}
